import { useState } from 'react';
import {
  Calendar, Plus, Users, MapPin, Clock, Trophy, BarChart3,
  Globe, Zap, ChevronRight, MoreHorizontal, Ticket
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

const EVENTS = [
  {
    id: 1, title: 'AI Startup Hackathon 2026', type: 'Hackathon', date: 'May 15-16, 2026',
    location: 'Online + Patna Hub', registrations: 234, capacity: 500, status: 'upcoming',
    prize: '₹5 Lakh', teams: 48
  },
  {
    id: 2, title: 'Startup Pitch Night — Bihar', type: 'Networking', date: 'Apr 28, 2026',
    location: 'BSFC Convention Center, Patna', registrations: 87, capacity: 150, status: 'upcoming',
    prize: null, teams: null
  },
  {
    id: 3, title: 'ML Workshop: Foundations', type: 'Workshop', date: 'Apr 10, 2026',
    location: 'Online', registrations: 320, capacity: 300, status: 'completed',
    prize: null, teams: null
  },
];

const EventCard = ({ event }) => (
  <Card className="border-border hover:shadow-card transition-shadow">
    <CardContent className="p-5">
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="font-semibold text-foreground">{event.title}</h3>
          </div>
          <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
            <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" />{event.date}</span>
            <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{event.location}</span>
            <span className="flex items-center gap-1"><Ticket className="w-3.5 h-3.5" />{event.registrations}/{event.capacity} registered</span>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
            event.type === 'Hackathon' ? 'bg-primary/10 text-primary border border-primary/20' :
            event.type === 'Workshop' ? 'bg-chart-2/10 text-chart-2 border border-chart-2/20' :
            'bg-chart-3/10 text-chart-3 border border-chart-3/20'
          }`}>{event.type}</span>
          <span className={event.status === 'upcoming' ? 'badge-info' : event.status === 'live' ? 'badge-success' : 'badge-muted'}>
            {event.status}
          </span>
        </div>
      </div>

      {event.type === 'Hackathon' && (
        <div className="flex items-center gap-4 mb-3 p-3 rounded-lg bg-muted/50 border border-border">
          <div className="flex items-center gap-2">
            <Trophy className="w-4 h-4 text-chart-4" />
            <span className="text-sm font-semibold text-foreground">Prize Pool: {event.prize}</span>
          </div>
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-chart-2" />
            <span className="text-sm text-muted-foreground">{event.teams} teams registered</span>
          </div>
        </div>
      )}

      <div className="flex items-center gap-2">
        <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
          <div
            className="h-full gradient-primary rounded-full transition-all"
            style={{ width: `${Math.min(100, (event.registrations / event.capacity) * 100)}%` }}
          />
        </div>
        <span className="text-xs text-muted-foreground">{Math.round((event.registrations / event.capacity) * 100)}% capacity</span>
      </div>

      <div className="flex gap-2 mt-4">
        <Button variant="outline" size="sm" className="flex-1 border-border text-xs">View Details</Button>
        {event.status === 'upcoming' && (
          <Button size="sm" className="flex-1 gradient-primary text-primary-foreground border-0 text-xs">Manage</Button>
        )}
        {event.status === 'completed' && (
          <Button variant="outline" size="sm" className="flex-1 border-border text-xs"><BarChart3 className="w-3.5 h-3.5 mr-1" />Analytics</Button>
        )}
      </div>
    </CardContent>
  </Card>
);

export default function Events() {
  const [open, setOpen] = useState(false);

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Events & Hackathons</h1>
          <p className="text-muted-foreground text-sm mt-1">Create and manage events, hackathons, and workshops via EventNexus</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="gradient-primary text-primary-foreground border-0 gap-2">
              <Plus className="w-4 h-4" /> Create Event
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>Create New Event</DialogTitle></DialogHeader>
            <div className="space-y-4 mt-2">
              <div><Label>Event Title</Label><Input placeholder="e.g. AI Startup Hackathon" className="mt-1" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Event Type</Label>
                  <Select>
                    <SelectTrigger className="mt-1"><SelectValue placeholder="Type" /></SelectTrigger>
                    <SelectContent>
                      {['Hackathon', 'Workshop', 'Networking', 'Conference', 'Webinar'].map(t =>
                        <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Date</Label><Input type="date" className="mt-1" /></div>
              </div>
              <div><Label>Location / Platform</Label><Input placeholder="Online / Venue address" className="mt-1" /></div>
              <div><Label>Capacity</Label><Input type="number" placeholder="Max registrations" className="mt-1" /></div>
              <div><Label>Description</Label><Textarea placeholder="Event details, agenda, prizes..." className="mt-1" rows={3} /></div>
              <div className="flex justify-end gap-3">
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button className="gradient-primary text-primary-foreground border-0" onClick={() => { toast.success('Event created!'); setOpen(false); }}>Create Event</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Total Events', value: '3', icon: Calendar, color: 'text-chart-1', bg: 'bg-chart-1/10' },
          { label: 'Total Registrations', value: '641', icon: Users, color: 'text-chart-2', bg: 'bg-chart-2/10' },
          { label: 'Hackathons Hosted', value: '1', icon: Trophy, color: 'text-chart-4', bg: 'bg-chart-4/10' },
          { label: 'Events Limit', value: '1/mo', icon: Zap, color: 'text-primary', bg: 'bg-primary/10' },
        ].map(stat => (
          <Card key={stat.label} className="border-border">
            <CardContent className="p-4">
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center mb-3 ${stat.bg}`}>
                <stat.icon className={`w-4 h-4 ${stat.color}`} />
              </div>
              <div className="text-xl font-bold text-foreground">{stat.value}</div>
              <div className="text-xs text-muted-foreground">{stat.label}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Tabs defaultValue="all">
        <TabsList className="bg-muted">
          <TabsTrigger value="all">All Events</TabsTrigger>
          <TabsTrigger value="upcoming">Upcoming</TabsTrigger>
          <TabsTrigger value="hackathons">Hackathons</TabsTrigger>
          <TabsTrigger value="completed">Completed</TabsTrigger>
        </TabsList>
        <TabsContent value="all" className="mt-6 space-y-4">
          {EVENTS.map(e => <EventCard key={e.id} event={e} />)}
        </TabsContent>
        <TabsContent value="upcoming" className="mt-6 space-y-4">
          {EVENTS.filter(e => e.status === 'upcoming').map(e => <EventCard key={e.id} event={e} />)}
        </TabsContent>
        <TabsContent value="hackathons" className="mt-6 space-y-4">
          {EVENTS.filter(e => e.type === 'Hackathon').map(e => <EventCard key={e.id} event={e} />)}
        </TabsContent>
        <TabsContent value="completed" className="mt-6 space-y-4">
          {EVENTS.filter(e => e.status === 'completed').map(e => <EventCard key={e.id} event={e} />)}
        </TabsContent>
      </Tabs>
    </div>
  );
}
